import { useEffect, useMemo, useRef } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';
import { IDisposable, languages } from 'monaco-editor';
import Pluralize from 'pluralize';
import { useAvContext } from '../../../context/AvContextProvider';
import { orderByKey } from '../../../utils/utils';
import { FormulaType, GroupingRulesSet, SubAutoComplete } from './types';

export const useGroupingRulesSet = projectionName =>
  useGqlQuery<GroupingRulesSet[]>({
    queryKey: [QueryKey.GroupingRules, projectionName],
    query: GET_LIST,
    variables: { projectionName },
    gcTime: 0,
    select: data =>
      orderByKey(
        data.map(ruleSet => ({
          ...ruleSet,
          rules: ruleSet.rules.map(rule => {
            const { condition, ...rest } = rule;
            return condition ? rule : rest;
          }),
        })),
        'name'
      ),
  });

export const useGetPluralizedAggregatedProjName = (projName: string) => {
  const { aggProjs } = useAvContext().accountEntities;
  const aggregatedProjName = aggProjs[projName].aggregates.name;
  const aggregatedProjDisplayName = aggProjs[aggregatedProjName].projDisplayName;
  return Pluralize(aggregatedProjDisplayName);
};

const GET_LIST = gql`
  query getRuleSetsV3($projectionName: String!) {
    getRuleSetsV3(projectionName: $projectionName) {
      id
      name
      isActive
      projectionName
      updatedAt
      updatedByUserId
      createdByUserId
      createdAt
      updatedAt
      rules {
        id
        name
        condition
        entityTitleFormat
        entityTitleFormulaType
        defaultFallbackRule
        createForEach
        grouping {
          fieldGrouping {
            fields
          }
        }
        ruleOrder
        timeFrame {
          hoursBefore
        }
      }
    }
  }
`;

export const CREATE_RULE_SET = gql`
  mutation createRuleSetV3($ruleSet: GroupingRuleSetInput) {
    createRuleSetV3(ruleSet: $ruleSet)
  }
`;

export const UPDATE_RULE_SET = gql`
  mutation updateRuleSetV3($ruleSet: GroupingRuleSetInput) {
    updateRuleSetV3(ruleSet: $ruleSet)
  }
`;

const isBetweenChars = (startChars, endChars, str, cursorPosition) => {
  // eslint-disable-next-line fabric-ui/no-let
  let inside = false;
  // eslint-disable-next-line fabric-ui/no-let
  for (let i = 0; i < str.length; i++) {
    if (i >= cursorPosition) {
      return inside;
    }
    if (str.slice(i, i + startChars.length) === startChars) {
      inside = true;
      i = i + startChars.length - 1;
    } else if (str.slice(i, i + endChars.length) === endChars) {
      inside = false;
      i = i + endChars.length - 1;
    }
  }
  return false;
};
export const isBetweenDoubleCurly = (model, position) =>
  isBetweenChars('{{', '}}', model.getLineContent(position.lineNumber), position.column - 1);

const createCompletionItems = completions =>
  completions
    .toSorted((a, b) => a.group.localeCompare(b.group))
    .map(item => ({
      label: item.title,
      kind: languages.CompletionItemKind.Field,
      insertText: item.value, // Use the 'value' property as the inserted value
      range: null, // Set the range if needed, else leave as null
    }));

export const useFieldSuggestions = (projectionName, usage, fieldOptions) => {
  const {
    accountEntities: { ingressProjs, aggProjs },
  } = useAvContext();

  const completions = useMemo(() => {
    if (fieldOptions) {
      return fieldOptions;
    }
    const subProj = SubAutoComplete[projectionName];
    const subTitleFilterOptions = (subProj && aggProjs[subProj].fieldList.SPF.filter(f => !f.visibilityConfig.config.hidden)) || [];
    const titleFilterOptions = (ingressProjs[projectionName] || aggProjs[projectionName]).fieldList[usage];
    return subProj ? [...titleFilterOptions, ...subTitleFilterOptions] : titleFilterOptions;
  }, [projectionName]);

  return createCompletionItems(completions);
};

export const useGetProjectionFieldCompletions: ({
  projectionName,
  isInteractive,
}: {
  projectionName: any;
  isInteractive: boolean;
}) => (editor, monaco) => void = ({ projectionName, isInteractive }) => {
  const disposeOptionsRef = useRef<IDisposable>(undefined);
  const { aggProjs } = useAvContext().accountEntities;
  const subProj = SubAutoComplete[projectionName];
  const subTitleFilterOptions = (subProj && aggProjs[subProj].fieldList.SPF.filter(f => !f.visibilityConfig.config.hidden)) || [];
  const fieldListType = isInteractive ? 'INTERACTIVE' : 'EVAL';

  const titleFilterOptions = aggProjs[projectionName]?.fieldList[fieldListType];

  useEffect(() => () => disposeOptionsRef.current?.dispose(), []);
  return (editor, monaco) => {
    const completions = subProj ? [...titleFilterOptions, ...subTitleFilterOptions] : titleFilterOptions;

    // Register the completion provider with Monaco Editor
    disposeOptionsRef.current = monaco.languages.registerCompletionItemProvider('python', {
      provideCompletionItems: (model, position) => {
        const lineContent = model.getLineContent(position.lineNumber);
        const prefix = lineContent.substring(0, position.column - 2);
        const suffix = lineContent.substring(position.column);

        return { suggestions: prefix.endsWith('{{') && suffix.startsWith('}') ? createCompletionItems(completions) : [] };
      },
    });
  };
};

export const ticketTitleRadioOptions = [
  { value: FormulaType.SMART_TEXT, title: 'Smart Text' },
  { value: FormulaType.CEL_EXPRESSION, title: 'Expression' },
];
